import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import usePoll from "../../../hooks/usePoll";
import api from "../../../services/api";

const YARA_ENABLED = window.jsbridge.settings.FEATURE_YARA_RETRO;
const YARA_RUNNING = window.jsbridge.enums.yaraRetroHuntStatus.RUNNING;
const REFRESH_RATE = 5000;

const cloudStatus = status => {
  switch (status) {
    case window.jsbridge.enums.yaraRulesetStatus.ERROR:
      return "Ruleset can not be processed due to errors.";
    case window.jsbridge.enums.yaraRulesetStatus.ACTIVE:
      return "Ruleset is active.";
    case window.jsbridge.enums.yaraRulesetStatus.DISABLED:
      return "Ruleset is disabled.";
    case window.jsbridge.enums.yaraRulesetStatus.DELETED:
      return "Ruleset has been deleted.";
    case window.jsbridge.enums.yaraRulesetStatus.PENDING:
      return "Ruleset is being validated.";
    case window.jsbridge.enums.yaraRulesetStatus.INVALID:
      return "Ruleset is invalid";
    case window.jsbridge.enums.yaraRulesetStatus.CAPPED:
      return "Maximum number of Spectra Intelligence matches reached. New Spectra Intelligence matches will not be stored";
    default:
      return "Status unknown.";
  }
};

const retroStatus = data => {
  switch (data.retrohunt_status) {
    case window.jsbridge.enums.yaraRetroHuntStatus.IDLE:
      return "Ruleset is disabled.";
    case window.jsbridge.enums.yaraRetroHuntStatus.VALIDATING:
      return "Ruleset is being validated.";
    case window.jsbridge.enums.yaraRetroHuntStatus.ERROR:
      return "Ruleset can not be processed due to errors.";
    case window.jsbridge.enums.yaraRetroHuntStatus.READY:
      return "Ruleset is ready for YARA Retro Hunt";
    case window.jsbridge.enums.yaraRetroHuntStatus.RUNNING:
      return (
        `YARA Retro Hunt running` +
        ` started: ${new Date(
          data.retrohunt_start_time
        ).toLocaleString()} estimated finish: ${
          data.retrohunt_estimated_finish_time
            ? new Date(data.retrohunt_estimated_finish_time).toLocaleString()
            : "Unknown"
        }`
      );
    case window.jsbridge.enums.yaraRetroHuntStatus.COMPLETE:
      return (
        `YARA Retro Hunt completed` +
        ` started: ${new Date(
          data.retrohunt_start_time
        ).toLocaleString()} finished: ${
          data.retrohunt_finish_time
            ? new Date(data.retrohunt_finish_time).toLocaleString()
            : "Unknown"
        }`
      );
    case window.jsbridge.enums.yaraRetroHuntStatus.CANCELLED:
      return "YARA Retro Hunt has been cancelled.";
    case window.jsbridge.enums.yaraRetroHuntStatus.CLEARED:
      return "YARA Retro Hunt results have been cleared.";
    default:
      return "Status unknown.";
  }
};

const renderYaraLocalIcon = data =>
  data ? (
    <span
      className="stickers stickers--gray stickers--local"
      data-role="tooltip"
      title={cloudStatus(data.scale_status)}
    >
      LOCAL
      <span
        className={`icon-local-state-${data.scale_status} icon--stickers`}
      />
    </span>
  ) : null;

const renderYaraCloudIcon = data =>
  data?.cloud ? (
    <span
      className="stickers stickers--gray stickers--cloud"
      title={cloudStatus(data.cloud_status)}
    >
      CLOUD
      <span
        className={`icon-cloud-state-${data.cloud_status} icon--stickers`}
      />
    </span>
  ) : null;

const renderYaraRetroIcon = data =>
  data?.cloud && YARA_ENABLED ? (
    <span
      className="stickers stickers--gray stickers--retrohunt"
      title={retroStatus(data)}
    >
      RETRO
      <span
        className={`icon-retrohunt-state-${data?.retrohunt_status} icon--stickers`}
      />
    </span>
  ) : null;

const renderYaraRetroRunningIcon = data =>
  data?.cloud && YARA_ENABLED && YARA_RUNNING === data?.retrohunt_status ? (
    <span className="rl-progress ms-5" title={retroStatus(data)}>
      <span className="rl-progress__number">
        {data?.retrohunt_progress || 0}%
      </span>
      <span
        className="rl-progress__bar"
        style={{ width: `${data?.retrohunt_progress || 0}%` }}
      />
    </span>
  ) : null;

const YaraInfoPanel = ({ ruleId, infoActive, infoAdded, infoEdited }) => {
  const [infoData, setInfoData] = useState();
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => (mounted.current = false);
  }, []);

  usePoll(() => {
    api
      .yaraAction({ id: ruleId, action: "Refresh" })
      .then(response => response.data)
      .then(data => {
        if (!mounted.current) return;
        setInfoData(data.data);
      })
      .catch(() => {
        if (!mounted.current) return;
        setInfoData("error");
      });
  }, REFRESH_RATE);
  return (
    <div className="yara-edit-info">
      <div className="yara-info">
        <span>{infoActive}</span>
        <div>
          <div>{infoAdded}</div>
          <div>{infoEdited}</div>
        </div>
      </div>
      <div className="yara-editor-ruleset-status">
        {renderYaraLocalIcon(infoData)}
        {renderYaraCloudIcon(infoData)}
        {renderYaraRetroIcon(infoData)}
        {renderYaraRetroRunningIcon(infoData)}
      </div>
    </div>
  );
};
YaraInfoPanel.propTypes = {
  ruleId: PropTypes.number.isRequired,
  infoActive: PropTypes.string,
  infoAdded: PropTypes.string,
  infoEdited: PropTypes.string
};
export default YaraInfoPanel;
